import { useEffect } from 'react'

import { highlightEvent } from '../slices/liveEventModalSlice'
import { createHighlightScrollOptions } from '../utils/highlightScrollOptions'

type HighlightedEvent = ReturnType<typeof highlightEvent>['payload']
type HighlightScrollInputs = Parameters<typeof createHighlightScrollOptions>[0]['getInputs']
type StartHighlightScroll = (options: ReturnType<typeof createHighlightScrollOptions>) => void

type UseHighlightScrollOnHighlightParams = {
  highlightedEvent: HighlightedEvent
  startHighlightScroll: StartHighlightScroll
  getInputs: HighlightScrollInputs
  headerOffset: number
}

/**
 * Starts the highlighted row scroll as soon as the Highlight button is clicked, instead of when the page has finished
 * loading and moved the calendar. The scroll loop waits for the row and the layout on its own, so it does not need the
 * page to be idle first, and starting it here means every click starts it: the click always sets a new `meta` object,
 * whether or not the time range or the URL changes afterwards.
 */
export const useHighlightScrollOnHighlight = ({ highlightedEvent, startHighlightScroll, getInputs, headerOffset }: UseHighlightScrollOnHighlightParams) => {
  const { id, meta } = highlightedEvent

  useEffect(() => {
    if (!id || !meta) return

    startHighlightScroll(
      createHighlightScrollOptions({
        trackedGameId: meta.trackedGameId,
        eventType: meta.eventType,
        highlightedEventId: id,
        eventStartTime: meta.eventStartTime,
        headerOffset,
        getInputs,
      })
    )
  }, [id, meta, startHighlightScroll, getInputs, headerOffset])
}
